/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI',
	'../../../../util/ContentUtil'
], (_, stringResources, APIFactory, DataPointActionsProviderAPI, ContentActionsProviderAPI, ContentUtil) => {

	const PROPERTYNAME = 'hiddenRowsAndColumns';
	const LOCALVIS = ['crosstab'];

	class HideRowColumnAction {
		constructor({ dashboardAPI, content, features }) {
			this.dashboard = dashboardAPI || features && features.API;
			this.features = features;
			if (content) {
				// dataPointAction will pass in the content api
				this.content = content;
				if (features) {
					features.DataPointActions.registerProvider('HideRowColumnAction', this.getAPI());
				}
			} else {
				features && features.ContentActions.registerProvider('HideRowColumnAction', this.getAPI());
			}
		}

		_getIcons() {
			return this.features['Icons'] || this.features['Dashboard.Icons'];
		}

		getDataPointActionList(selections, options) {
			if (this._isLocalVis() && this._supportDataPointAction() && options.isAreaSelected) {
				this.selectedArea = ContentUtil.getContextFromSelection(this.content, this.dashboard, selections);
				// if the selection is not valid, disable the action
				if (!this.selectedArea.length) {
					return [];
				}
				this.isAreaSelected = true;
				const iconHide = this._getIcons().getIcon('iconHide');
				return this._getContribution('Datapoint', stringResources.get('hideRowColumn'), iconHide, false);
			}
			return [];
		}

		_supportDataPointAction() {
			if (this.content.getFeature('state').getError() || this.dashboard.getMode() !== this.dashboard.MODES.EDIT) {
				return false;
			}
			return true;
		}

		/**
		 * widgetAction interface
		 * @param {*} idList
		 */
		getContentActionList(idList) {
			const contentId = idList[0];
			this.content = this.dashboard.getCanvas().getContent(contentId);
			if (this._isLocalVis() && this._supportContentAction(idList)) {
				const iconView = this._getIcons().getIcon('iconView');
				return this._getContribution('Content', stringResources.get('showAllRowColumn'), iconView, this._isHidden());
			}
			return [];
		}

		_getContribution(actionOrigin, actionName, icon, isDisabled) {
			let handler;
			return [{
				name: 'hideRowColumnAction' + actionOrigin,
				label: actionName,
				text: actionName,
				icon: icon.id,
				type: 'Button',
				closeOnActionApplied: true,
				disabled: isDisabled,
				section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_VIZ_FORMAT,
				order: 30,
				actions: {
					apply: this.onStateChange.bind(this)
				},
				callbacks: {
					registerCallback: (button) => {
						handler = ()=>{
							button.updateState({ disabled: this._isHidden() });
						};
						this.content.on('change:property:hiddenRowsAndColumns', handler);
					},
					unregisterCallback: () => {
						this.content.off('change:property:hiddenRowsAndColumns', handler);
					}
				}
			}];
		}

		_supportContentAction(idList) {
			if (idList && idList.length > 1 ||
				this.content.getFeature('state').getError() ||
				this.dashboard.getMode() !== this.dashboard.MODES.EDIT) {
				return false;
			}
			return true;
		}

		_isLocalVis() {
			const visFeature = this.content.getFeature('Visualization');
			const visId = visFeature && visFeature.getDefinition().getId();
			return !!visId && _.contains(LOCALVIS, visId);
		}

		onStateChange() {
			let propertyList = [];
			propertyList = this._buildPropertyList(propertyList);

			const transactionToken = this.dashboard.getFeature('Transaction').startTransaction();
			this.setProperties(propertyList, transactionToken);
			this._removeFilters(transactionToken);
			this.dashboard.getFeature('Transaction').endTransaction(transactionToken);
		}

		_isHidden() {
			return _.isEmpty(this.content.getPropertyValue(PROPERTYNAME) || []);
		}

		_buildPropertyList(propertyList) {
			let newPropertyVal = [];
			if (this.isAreaSelected) {
				let currentPropertyValue = this.content.getPropertyValue(PROPERTYNAME) || [];
				newPropertyVal = _.union(this.selectedArea, currentPropertyValue);
			}
			propertyList.push({ 'id': PROPERTYNAME,'value': newPropertyVal });
			return propertyList;
		}

		/**
		 * This method sets the vis properties to the value given
		 * @param propertyList - the propertyList that trigerred the state change
		 */
		setProperties(propertyList, transactionToken) {
			propertyList.forEach(prop => {
				this.content.setPropertyValue(prop.id, prop.value, transactionToken);
			});
		}

		_removeFilters(transactionToken) {
			this.dashboard.getFeature('GlobalFilters').removeFilters(ContentUtil.getSelectors(this.content, this.dashboard), transactionToken);
		}

		getAPI() {
			if (!this._api) {
				return APIFactory.createAPI(this, [DataPointActionsProviderAPI, ContentActionsProviderAPI]);
			}
			return this._api;
		}
	}
	return HideRowColumnAction;
});
